import { Dexie, InsertType, type EntityTable } from "dexie";
import {
	OfflinePassScan,
	OfflineTicket,
	PassScan,
	Product,
	Revocation,
	Scan,
	SeasonPass,
	Singleton,
	Ticket,
	Event,
} from "./entities";

// NOTE: Don’t declare all columns like in SQL on the .stores().
// You only declare properties you want to index, that is properties you want to use in a where() or get() query.
//
// ++	    Auto-incremented primary key
// &	    Unique
// *	    Multi-entry index
// [A+B]	Compound index
// https://dexie.org/docs/Version/Version.stores()
//
// The entity classes from entities.ts should contain all properties.

export default class DB extends Dexie {
	// Stores singleton records like scanner configuration and booth options
	singletons!: EntityTable<Singleton, "key">;

	// Stores offline scans until they are synced to api
	offlinetickets!: EntityTable<OfflineTicket, "id">;

	// Stores valid tickets for this config fetched from api
	tickets!: EntityTable<Ticket, "ticket_code">;

	// Stores scans fetched from api
	scans!: EntityTable<Scan, "id">;

	// Stores revoked (cancelled/refunded) ticket ids fetched from api
	revocations!: EntityTable<Revocation, "ticket_id">;

	// Stores event data fetched from api
	events!: EntityTable<Event, "slug">;

	// Stores data about product variants from the active events
	products!: EntityTable<Product, "id">;

	// Stores season passes (full snapshot per sync) for the config's season events
	season_passes!: EntityTable<SeasonPass, "id">;

	// Stores season pass scans fetched from api (cross-device dedup)
	pass_scans!: EntityTable<PassScan, "id">;

	// Stores offline pass scans until they are synced to api
	offline_pass_scans!: EntityTable<OfflinePassScan, "id">;

	constructor() {
		super("liveto-scanner-v2", { autoOpen: true, chromeTransactionDurability: "relaxed" });
		this.version(33)
			.stores({
				// code is a non-unique index on purpose: RENEWED rows reuse the code.
				season_passes: "&id,code,ticket_code",
				pass_scans: "&id,season_pass_id",
				offline_pass_scans: "++id,season_pass_id",
			})
			.upgrade(async tx => {
				const lastSync = await tx.table("singletons").get("lastSync");
				await tx
					.table("singletons")
					.put(
						{ ...(lastSync ?? { lastTicketId: 0, lastScanId: 0, lastRevocationId: 0 }), lastPassScanId: 0 },
						"lastSync"
					);
			});
		this.version(32)
			.stores({ revocations: "&ticket_id" })
			.upgrade(async tx => {
				// Seed the revocation cursor onto the existing lastSync singleton.
				const lastSync = await tx.table("singletons").get("lastSync");
				await tx
					.table("singletons")
					.put({ ...(lastSync ?? { lastTicketId: 0, lastScanId: 0 }), lastRevocationId: 0 }, "lastSync");
			});
		this.version(31)
			.stores({
				singletons: ",key",
				offlinetickets: "++id,[ticket_id+scanned],[scan_type+ticket_id]",
				tickets: "&ticket_code,&id",
				scans: "&id,[ticket_id+scanned],[scan_type+ticket_id],device_label",
				events: "&slug",
				products: "&id,event_slug,product_category",

				// TODO: can be removed after all clients are migrated from v30
				configuration: null,
				booth: null,
				userSettings: null,
				scanSettings: null,
				scanMode: null,
			})
			.upgrade(tx => {
				// Combine tables that only hold one row
				// TODO: can be removed after all clients are migrated from v30
				const tables = ["configuration", "booth", "userSettings", "scanSettings", "scanMode"];
				return Promise.all([
					...tables.map(async table =>
						tx
							.table(table)
							.toCollection()
							.first()
							.then(value => tx.table("singletons").put(value, table))
					),
					tx.table("singletons").put({ lastTicketId: 0, lastScanId: 0 }, "lastSyncIds"),
					tx
						.table("events")
						.toArray()
						.then(events =>
							events.flatMap(event =>
								event.products.flatMap(
									(product: {
										variants: InsertType<Product, never>[];
										id: number;
										name: string;
										valid_from: string;
										valid_until: string;
									}) =>
										product.variants.flatMap(async variant =>
											tx.table("products").put({
												...variant,
												id: variant.id,
												event_slug: event.slug,
												product_category: product.id,
												product_category_name: product.name,
												valid_from: product.valid_from,
												valid_until: product.valid_until,
											})
										)
								)
							)
						),
				]);
			});
		this.singletons.mapToClass(Singleton);
		this.offlinetickets.mapToClass(OfflineTicket);
		this.tickets.mapToClass(Ticket);
		this.scans.mapToClass(Scan);
		this.revocations.mapToClass(Revocation);
		this.events.mapToClass(Event);
		this.products.mapToClass(Product);
		this.season_passes.mapToClass(SeasonPass);
		this.pass_scans.mapToClass(PassScan);
		this.offline_pass_scans.mapToClass(OfflinePassScan);

		// TODO: can be removed after all clients are migrated from v30
		this.version(30).stores({
			configuration: "&apikey",
			booth: "&apikey",
			userSettings: "++id",
			scanSettings: "++id",
			scanMode: "++id",
			offlinetickets: "++id,ticket_id",
			tickets: "&ticket_code,&id",
			scans: "&id,ticket_id,device_label",
			events: "&slug",
		});
	}
}
